import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * MetaPixelLoader — production-only Meta (Facebook) Pixel loader.
 *
 * Design mirrors GA4Loader:
 *  - Pixel ID comes from REACT_APP_META_PIXEL_ID env var (compile-time).
 *  - Loads only on production hostnames (venturekhoj.com / www.venturekhoj.com).
 *    Local dev + preview stay silent so Meta stats aren't polluted.
 *  - `?fbq=1` query flag force-enables the pixel for smoke tests.
 *  - Fires `PageView` on initial mount and every SPA route change.
 *  - Exposes `window.vkFbq(event, params)` so any component can fire standard
 *    events (ViewContent, Lead, InitiateCheckout, Purchase, …) without imports.
 */

const PROD_HOSTNAMES = ["venturekhoj.com", "www.venturekhoj.com"];

function isProdHost() {
  if (typeof window === "undefined") return false;
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.get("fbq") === "1") return true;
  } catch (_) { /* noop */ }
  return PROD_HOSTNAMES.includes(window.location.hostname);
}

function isValidPixelId(id) {
  return typeof id === "string" && /^\d{6,}$/.test(id);
}

function injectFbq(pixelId) {
  if (typeof document === "undefined") return;
  if (document.getElementById("meta-pixel-loader")) return;

  // Official Meta Pixel base code (adapted to be idempotent + tagged).
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.id = "meta-pixel-loader";
    t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq("init", pixelId);
}

/** Public helper — window.vkFbq('Lead', { content_name: 'signup' }) */
if (typeof window !== "undefined") {
  window.vkFbq = (event, params = {}) => {
    try { window.fbq && window.fbq("track", event, params); } catch (_) { /* noop */ }
  };

  // ---------------------------------------------------------------------
  // Deduplicated Browser + Server helper.
  //   window.vkFbqDedup('Lead', { value: 0, currency: 'INR', ... }, { email, external_id })
  // Generates a shared event_id, fires fbq with eventID, and POSTs to
  // /api/meta/capi/track so the server-side CAPI event carries the SAME
  // event_id — Meta then deduplicates the browser + server pair.
  // Returns the event_id used (uuid v4).
  // ---------------------------------------------------------------------
  window.vkFbqDedup = (eventName, customData = {}, userHints = {}) => {
    // uuid v4 without pulling a dep
    const eventId = ("10000000-1000-4000-8000-100000000000").replace(/[018]/g, (c) =>
      // eslint-disable-next-line no-mixed-operators
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
    // Fire browser event with eventID for dedup
    try {
      window.fbq && window.fbq("track", eventName, customData, { eventID: eventId });
    } catch (_) { /* noop */ }
    // Relay to backend so it fires the matching server event
    try {
      const cookie = (n) => {
        const m = document.cookie.match(new RegExp("(?:^|; )" + n + "=([^;]*)"));
        return m ? decodeURIComponent(m[1]) : undefined;
      };
      const body = {
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        email: userHints.email,
        phone: userHints.phone,
        external_id: userHints.external_id,
        fbp: cookie("_fbp"),
        fbc: cookie("_fbc"),
        custom_data: customData,
      };
      // Only send when pixel is enabled (prod host or ?fbq=1 override). Preview
      // stays quiet so we don't pollute Meta with dev noise.
      if (isProdHost()) {
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${apiBase}/api/meta/capi/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => { /* fire-and-forget */ });
      }
    } catch (_) { /* noop */ }
    return eventId;
  };
}

export default function MetaPixelLoader() {
  const location = useLocation();
  const readyRef = useRef(false);

  // Boot once — inject fbq if gates pass.
  useEffect(() => {
    const pixelId = process.env.REACT_APP_META_PIXEL_ID;
    if (!isProdHost()) {
      // eslint-disable-next-line no-console
      console.info("[MetaPixel] skipped — non-prod host", typeof window !== "undefined" ? window.location.hostname : "");
      return;
    }
    if (!isValidPixelId(pixelId)) {
      // eslint-disable-next-line no-console
      console.info("[MetaPixel] skipped — invalid or missing REACT_APP_META_PIXEL_ID");
      return;
    }
    injectFbq(pixelId);
    readyRef.current = true;
    try { window.fbq("track", "PageView"); } catch (_) { /* noop */ }
  }, []);

  // SPA route change → PageView.
  useEffect(() => {
    if (!readyRef.current || !window.fbq) return;
    Promise.resolve().then(() => {
      try { window.fbq("track", "PageView"); } catch (_) { /* noop */ }
    });
  }, [location.pathname, location.search]);

  return null;
}
